import React from "react";
import { makeStyles } from "tss-react/mui";

import { Box, BoxProps, CircularProgress, Theme, Typography } from "@mui/material";
import { Tooltip, TooltipProps } from "@shared/Tooltip";

// Two kinds of spinner, see "Spinners" in .claude/conventions/ui.md:
// - LoadingIcon / LoadingBox: the user is waiting for something to finish before they can carry on.
// - ActivityIcon: work runs on the backend or on a device, and nothing on screen waits for it.

const loadingSizes = { inline: 16, block: 40, page: 70 } as const;

type LoadingSize = keyof typeof loadingSizes;

const useStyles = makeStyles<{ activityColor?: string }>()((theme: Theme, { activityColor }) => ({
    activityIcon: {
        color: activityColor,
    },
    loadingBox: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    pageBox: {
        width: "100%",
        height: "100%",
    },
    progress: {
        position: "relative",
        display: "inline-flex",
        verticalAlign: "middle",
    },
    progressLabel: {
        position: "absolute",
        inset: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: theme.palette.emteria.text.secondary,
    },
}));

type LoadingIconProps = {
    size?: LoadingSize;
    value?: number;
    // For dark surfaces such as the log stream, where the brand colour would disappear.
    inverse?: boolean;
};

export function LoadingIcon({ size = "inline", value, inverse = false }: LoadingIconProps): React.JSX.Element {
    const { classes } = useStyles({});
    const isDeterminate = value !== undefined;

    return (
        <Box className={classes.progress} component="span">
            <CircularProgress
                variant={isDeterminate ? "determinate" : "indeterminate"}
                value={value}
                size={loadingSizes[size]}
                sx={(theme) => ({ color: inverse ? theme.palette.emteria.text.inverse : theme.palette.emteria.primary })}
            />
            {isDeterminate ? (
                <Typography
                    variant="body2"
                    component="span"
                    className={classes.progressLabel}
                >{`${Math.round(value)}%`}</Typography>
            ) : null}
        </Box>
    );
}

type LoadingBoxProps = BoxProps & {
    size?: Exclude<LoadingSize, "inline">;
    value?: number;
    inverse?: boolean;
};

export const LoadingBox = ({ size = "block", value, inverse, className, ...props }: LoadingBoxProps) => {
    const { classes, cx } = useStyles({});

    return (
        <Box {...props} className={cx(classes.loadingBox, size === "page" && classes.pageBox, className)}>
            <LoadingIcon size={size} value={value} inverse={inverse} />
        </Box>
    );
};

// For controls that take an icon class name instead of an element, such as an icon button's `iconClass`.
export const activityIconClass = "fa-solid fa-spinner fa-spin";

type ActivityIconProps = {
    tooltip?: TooltipProps["title"];
    tooltipPlacement?: TooltipProps["placement"];
    color?: string;
};

export const ActivityIcon = ({ tooltip, tooltipPlacement, color }: ActivityIconProps) => {
    const { classes, cx } = useStyles({ activityColor: color });
    const icon = <i className={cx(activityIconClass, classes.activityIcon)} />;

    return tooltip ? (
        <Tooltip title={tooltip} placement={tooltipPlacement}>
            {icon}
        </Tooltip>
    ) : (
        icon
    );
};
